import { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import LayoutContext from "../../store/adminStore/layoutStore/layout-context";
import Loading from "../UI/Loading/Loading";
import classes from "./ThemeLayout.module.css";
import ThemeSidebar from "./ThemeSidebar/ThemeSidebar";
import { LanguageContext } from "../../store/language-context";

const ThemeLayout = () => {
  const { userLanguage } = useContext(LanguageContext);
  const layoutCtx = useContext(LayoutContext);
  const { theme, changeTheme } = layoutCtx;
  const [openThemeSidebar, setThemeOpenSidebar] = useState(false);

  const swipableSidebarRef = useRef();
  const [swipVals, setSwipVals] = useState({
    sidebarWidth: 0,
    sidebarStartLeftX: 0,
    sidebarCurrentLeftX: 0,
    triggerPointX: 0,
    maxSidebarLeftX: 0,
    transformValue: "0px",
    clientStartX: 0,
    clientCurrentX: 0,
    isSwiping: false,
  });

  const navigate = useNavigate();
  const handleKeyPress = useCallback((event) => {
    if (event.ctrlKey && event.key === "b") {
      navigate("/operation");
    }
  }, []);
  useEffect(() => {
    // attach the event listener
    document.addEventListener("keydown", handleKeyPress);

    // remove the event listener
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    let viewportWidth = window.innerWidth;
    let sidebarWidth = swipableSidebarRef.current.offsetWidth;
    let triggerPointX = viewportWidth - sidebarWidth / 2;
    let maxSidebarLeftX = viewportWidth - sidebarWidth - 10;
    let sidebarStartLeftX = swipableSidebarRef.current.getBoundingClientRect().x;

    setSwipVals((ps) => ({
      ...ps,
      triggerPointX,
      maxSidebarLeftX,
      sidebarStartLeftX,
      sidebarWidth,
    }));
  }, []);

  const sidebarTouchStartHandler = (e) => {
    const changedTouch = e.changedTouches[0];
    let x = changedTouch.clientX;
    let sidebarCurrentLeftX = swipableSidebarRef.current.getBoundingClientRect().x;
    setSwipVals((ps) => ({
      ...ps,
      isSwiping: true,
      clientCurrentX: x,
      clientStartX: x,
      sidebarCurrentLeftX,
      sidebarStartLeftX: sidebarCurrentLeftX,
    }));
  };
  const sidebarTouchMoveHandler = (e) => {
    const changedTouch = e.changedTouches[0];
    let clientCurrentX = changedTouch.clientX;
    let sidebarCurrentLeftX = swipableSidebarRef.current.getBoundingClientRect().x;

    if (openThemeSidebar) {
      setSwipVals((ps) => {
        let sidebarStartLeftX = ps.sidebarCurrentLeftX + (ps.clientCurrentX - ps.clientStartX);
        let transformValue;
        if (sidebarStartLeftX > ps.maxSidebarLeftX) {
          transformValue = `${ps.clientCurrentX - ps.clientStartX}px`;
        } else {
          transformValue = "0px";
        }
        return { ...ps, clientCurrentX, transformValue, sidebarCurrentLeftX };
      });
    } else {
      setSwipVals((ps) => {
        let sidebarStartLeftX = ps.sidebarCurrentLeftX - (ps.clientCurrentX - ps.clientStartX);
        let transformValue;
        if (Math.abs(ps.clientCurrentX - ps.clientStartX) < ps.sidebarWidth) {
          transformValue = `${ps.clientCurrentX - ps.clientStartX - 8}px`;
        } else {
          transformValue = "-100%";
        }
        return { ...ps, clientCurrentX, transformValue, sidebarCurrentLeftX };
      });
    }
  };
  const sidebarTouchEndHandler = (e) => {
    setSwipVals((ps) => {
      // if (ps.sidebarCurrentLeftX > ps.triggerPointX && openThemeSidebar) {
      //   setThemeOpenSidebar(false);
      // } else if (ps.sidebarCurrentLeftX < ps.triggerPointX && !openThemeSidebar) {
      //   setThemeOpenSidebar(true);
      // }
      if (Math.abs(ps.clientCurrentX - ps.clientStartX) > ps.sidebarWidth / 2 && openThemeSidebar) {
        setThemeOpenSidebar(false);
      } else if (
        Math.abs(ps.clientCurrentX - ps.clientStartX) > ps.sidebarWidth / 2 &&
        !openThemeSidebar
      ) {
        setThemeOpenSidebar(true);
      }
      return { ...ps, isSwiping: false, transformValue: "0px" };
    });
  };

  return (
    <div
      className={[
        classes.layoutContainer,
        userLanguage === "pe" ? classes.rtlDir : classes.ltrDir,
        theme === "White"
          ? classes.whiteRootBackground
          : theme === "Pink"
            ? classes.pinkRootBackground
            : theme === "Gradient"
              ? classes.gradientRootBackground
              : theme === "Dark"
                ? classes.darkRootBackground
                : theme === "Blue"
                  ? classes.BlueRootBackground
                  : null,
      ].join(" ")}
    >
      {openThemeSidebar && (
        <div className={classes.backDrop} onClick={() => setThemeOpenSidebar((prev) => !prev)} />
      )}
      <div
        ref={swipableSidebarRef}
        onTouchStart={sidebarTouchStartHandler}
        onTouchEnd={sidebarTouchEndHandler}
        onTouchMove={sidebarTouchMoveHandler}
        className={[
          classes.themeSidebarContainer,
          openThemeSidebar ? classes.openSidebar : classes.closeSidebar,
          userLanguage === "pe" ? classes.sidebarContainerRtlLan : classes.sidebarContainerLtrLan,
          openThemeSidebar ? classes.opSb : userLanguage === "pe" ? classes.rtlCl : classes.ltrCl,
        ].join(" ")}
        style={
          swipVals.isSwiping
            ? openThemeSidebar
              ? { transform: `translateX(calc(${swipVals.transformValue}))` }
              : { transform: `translateX(calc(100% + ${swipVals.transformValue} - 8px))` }
            : null
        }
      >
        <ThemeSidebar
          userLanguage={userLanguage}
          theme={theme}
          changeTheme={changeTheme}
          setThemeOpenSidebar={setThemeOpenSidebar}
          openThemeSidebar={openThemeSidebar}
        />
      </div>
      <div
        className={[
          classes.mainContainer,
          openThemeSidebar ? classes.openMainContainer : classes.closeMainContainer,
          userLanguage === "pe" ? classes.MainContainerRtlLan : classes.MainContainerLtrLan,
        ].join(" ")}
      >
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};

export default ThemeLayout;
